import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  capacidadeDaMaquina,
  CHAVES_DE_CAPACIDADE,
  CODIGOS_ERRO,
  combinarModelos,
  FRASES_DOS_ERROS_DO_OPENCODE,
  modoDaMestreEhFixo,
  MODOS_PERMISSAO,
  modoDisponivelNaMaquina,
  modosDoMotor,
  ROTAS,
  tituloDaSessao,
  type IdModoPermissao,
} from '@remoto/shared';
import type { AnexoDaMensagem, DecisaoPermissao } from '@remoto/protocolo';
import { chamar, ErroApi } from '../../api';
import { mensagemDoErro } from '../../erros';
import { usarSessao } from '../../estado/sessao';
import type { ItemDeChat } from '../../sessoes/mensagens';
import { usarConversa, type Sessao } from '../../sessoes/conversa';
import {
  emEntrega,
  FRASE_PADRAO_DO_ENVIO,
  FRASE_SEM_CONFIRMACAO,
  FRASES_DO_ENVIO,
  motivoDaFalha,
  type NaFila,
} from '../../sessoes/fila-de-envio';
import {
  aceitarRespostaDoEnvio,
  enfileirarMensagem,
  novoIdDeMensagem,
  reabrirMensagem,
  recusarEnvio,
  SEM_MENSAGENS,
  tirarMensagem,
  tirarMensagensJaVistas,
  usarEnvios,
} from '../../sessoes/envios';
import { tamanhoLegivel } from '../../formato';
import { filtrarPorExibicao } from '../../sessoes/exibicao';
import { BotaoLeve } from '../layout';
import { usarConfirmacao } from '../Modal';
import {
  IconeAgente,
  IconeAviso,
  IconeDescer,
  IconeEsperando,
  IconeFechar,
  IconeMaisOpcoes,
} from '../icones';
import { Bolha, PilhaDePedidos } from './pecas';
import { PainelDeAgentes } from './PainelDeAgentes';
import { QuemVeEstaConversa } from './QuemVeEstaConversa';
import { DetalheDoUso, ResumoDoUso } from './UsoNoCabecalho';
import { Compositor } from './Compositor';
import {
  guardarMensagem,
  gravarExibicao,
  lerExibicao,
  recuperarMensagem,
  type Exibicao,
} from '../../workspace/memoria';
import { usarCapacidades } from '../../sessoes/capacidades';
import { Seletor } from '../Seletor';
import { ROTULO_DO_MOTOR, type Motor } from '@remoto/protocolo';
import { CaixaDeMarcar } from '../campos';
import { usarTelaAcesa } from '../../tela-acesa';
import { SeloForaDeCasa } from '../SeloForaDeCasa';
import { motorSaiDeCasa, origemDoCatalogo } from '../../sessoes/criacao-por-motor';
import { avisoDeImagemDoMotor } from '../../sessoes/imagem-no-motor';
import { motorDaLinha, provedorDaConversa, seloDoMotor } from '../../sessoes/motor-na-tela';
import { ModalDeTrocaDeMotor, usarDestinosDaTroca } from './TrocaDeMotor';
import { ModalDeModeloOpencode, rotuloDoModeloOpencode } from './ModeloDoOpencode';

/**
 * A linha da sessão mora em `sessoes/conversa.ts`, junto de quem a busca.
 *
 * Reexportada aqui porque `rotas/painel.tsx` já a importava deste módulo, e
 * mudar o endereço de um tipo não é assunto desta mudança.
 */
export type { Sessao };

/**
 * A conversa de uma sessão, inteira, sem depender de rota.
 *
 * Recebe o id por propriedade em vez de ler dos parâmetros da URL: assim a
 * mesma tela serve o painel da direita no workspace e a página cheia do
 * celular, sem duas cópias da mesma lógica de replay.
 */
export function PainelDaSessao({
  id,
  aoAbrirArquivo,
  aoContinuarNaPasta,
  aoIrParaSessao,
}: {
  id: string;
  aoAbrirArquivo?: (caminho: string) => void;
  aoContinuarNaPasta?: (sessao: Sessao) => void;
  /** Ir para outra conversa — usado pelo "continuar aqui". */
  aoIrParaSessao?: (sessaoId: string) => void;
}): React.JSX.Element {
  const cliente = useQueryClient();
  const [mensagem, setMensagem] = useState(() => recuperarMensagem(id));
  const [erroComando, setErroComando] = useState('');
  const fim = useRef<HTMLDivElement>(null);
  const grudado = useRef(true);
  const areaDeRolagem = useRef<HTMLDivElement>(null);
  /** Onde a leitura estava antes de o conteúdo crescer para cima. */
  const ancora = useRef<{ altura: number; topo: number } | null>(null);
  const papel = usarSessao((e) => e.usuario?.papel);

  /**
   * Os dados da conversa — replay, cursor, tempo real e paginação — vêm do
   * gancho, que a Prosa também usa. O que sobra aqui é desenho: rolagem,
   * rascunho, fila e "visto até aqui".
   */
  const {
    sessao,
    pedidos,
    itens,
    eventos,
    tarefas,
    temMais,
    temMaisNoDisco,
    erroDisco,
    carregando,
    carregarAnteriores,
    parcial,
    maiorSeq,
  } = usarConversa(id, {
    // A âncora de leitura é de quem tem o `ref` da área de rolagem, então o
    // gancho avisa e esta tela decide o que guardar.
    aoCrescerParaCima: () => {
      grudado.current = false;
      const area = areaDeRolagem.current;
      if (area) ancora.current = { altura: area.scrollHeight, topo: area.scrollTop };
    },
  });

  const responder = useMutation({
    mutationFn: (dados: { pedidoId: string; resposta: DecisaoPermissao }) =>
      chamar(`${ROTAS.permissoes}/${dados.pedidoId}/responder`, {
        method: 'POST',
        corpo: { pedidoId: dados.pedidoId, resposta: dados.resposta },
      }),
    onSettled: () => {
      void cliente.invalidateQueries({ queryKey: ['permissoes', id] });
      void cliente.invalidateQueries({ queryKey: ['permissoes-pendentes'] });
    },
    onError: (e) =>
      setErroComando(
        e instanceof ErroApi && e.codigo === 'pedido_ja_decidido'
          ? 'Este pedido já foi respondido — provavelmente na própria máquina.'
          : 'Não foi possível responder.',
      ),
  });

  /**
   * A fila do que você mandou e ainda não apareceu na conversa.
   *
   * O Claude Code só escreve a mensagem no transcript quando chega a vez dela.
   * Mandar algo enquanto a sessão trabalha limpava o campo e não mostrava nada
   * em lugar nenhum — parecia que a mensagem tinha sumido. Ela não sumia;
   * ficava esperando, invisível. Agora espera à vista, e dá para desistir.
   *
   * Mora no store de envios (`sessoes/envios.ts`), por conversa, e não neste
   * componente: a entrega de anexos grandes termina minutos depois, e a falha
   * dela tem de estar esperando quando a pessoa voltar à conversa — não ter
   * ido embora com a troca de id.
   */
  const fila = usarEnvios((e) => e.mensagens[id] ?? SEM_MENSAGENS);
  /** Alguma mensagem desta conversa ainda tem anexo a caminho da máquina: o Enviar fecha. */
  const entregando = emEntrega(fila);

  /**
   * A conversa aberta AGORA, para os callbacks do envio.
   *
   * Um `POST` pode voltar depois de a pessoa trocar de conversa, e o callback
   * carrega o `id` do render em que nasceu. Limpar o campo e o aviso "da
   * conversa atual" nesse momento apagava o rascunho de OUTRA conversa.
   */
  const idAberto = useRef(id);
  idAberto.current = id;

  const enviar = useMutation({
    mutationFn: (dados: NaFila) =>
      chamar<unknown>(`${ROTAS.sessoes}/${dados.sessaoId}/enviar`, {
        method: 'POST',
        corpo: { texto: dados.texto, anexos: dados.anexos },
      }),
    onSuccess: (resposta, dados) => {
      // O rascunho é de quem mandou; o campo e o aviso, de quem está olhando.
      guardarMensagem(dados.sessaoId, '');
      if (dados.sessaoId === idAberto.current) {
        setMensagem('');
        setErroComando('');
      }
      // `entrega` na resposta: a API aceitou a mensagem, mas a máquina ainda
      // baixa os anexos. O item fica "entregando" até o `sessao.entrega`.
      aceitarRespostaDoEnvio(dados.sessaoId, dados.id, resposta);
      if (dados.acordando) {
        // A API acordou a conversa antes de entregar: o estado mudou do lado
        // de lá, e o aviso de "dormiu" tem de sair sem esperar o próximo pulso.
        void cliente.invalidateQueries({ queryKey: ['sessao', dados.sessaoId] });
        void cliente.invalidateQueries({ queryKey: ['sessoes'] });
      }
    },
    onError: (e, dados) => {
      // A frase mora no ITEM, e não na linha de aviso do painel: a conversa pode
      // não estar aberta agora, e a pessoa tem de achá-la quando voltar.
      recusarEnvio(
        dados.sessaoId,
        dados.id,
        mensagemDoErro(e, FRASE_PADRAO_DO_ENVIO, FRASES_DO_ENVIO),
      );
    },
  });

  function enfileirar(texto: string, anexos: AnexoDaMensagem[]): void {
    // O fim da fila de verdade: sem isto o `/compact` do cabeçalho passaria
    // na frente da mensagem que ainda espera os anexos.
    if (entregando) return;
    const item: NaFila = {
      id: novoIdDeMensagem(),
      sessaoId: id,
      texto,
      anexos,
      // Mensagem para conversa dormindo a ACORDA (a API chama o mesmo Retomar).
      ...(dormindo ? { acordando: true } : {}),
    };
    enfileirarMensagem(item);
    enviar.mutate(item);
  }

  const comandar = useMutation({
    mutationFn: (acao: 'interromper' | 'fechar' | 'compactar') =>
      chamar(`${ROTAS.sessoes}/${id}/${acao}`, { method: 'POST' }),
    // Pelo código: `sessao_em_turno` (compactar no meio do turno) já tem frase no catálogo.
    onError: (e) => setErroComando(mensagemDoErro(e, 'A máquina não respondeu ao comando.')),
  });

  /**
   * O botão "Compactação automática do Remoto" desta conversa (R4). Vale para
   * todo mundo que a vê, então a mudança é otimista e a resposta da API manda:
   * errou, o cache volta ao que o servidor diz.
   */
  const trocarCompactacao = useMutation({
    mutationFn: (automatica: boolean) =>
      chamar(`${ROTAS.sessoes}/${id}/compactacao`, { method: 'POST', corpo: { automatica } }),
    onMutate: (automatica) => {
      cliente.setQueryData<Sessao | undefined>(['sessao', id], (antiga) =>
        antiga ? { ...antiga, compactacaoAutomatica: automatica } : antiga,
      );
    },
    onSettled: () => void cliente.invalidateQueries({ queryKey: ['sessao', id] }),
    onError: (e) =>
      setErroComando(mensagemDoErro(e, 'Não foi possível mudar a compactação automática.')),
  });

  const pararTarefa = useMutation({
    mutationFn: (tarefaId: string) =>
      chamar(`${ROTAS.sessoes}/${id}/parar-tarefa`, { method: 'POST', corpo: { tarefaId } }),
    onError: () => setErroComando('Não foi possível parar este subagente.'),
  });

  const trocarModo = useMutation({
    // `confirmacaoModoPerigoso` sai do seletor, nunca de uma constante: com
    // `true` fixo a guarda da API (`exigirModoPermitido`) não recusava nada, e a
    // garantia era só da tela. Mesmo defeito do Fable, no seletor vizinho.
    mutationFn: (dados: { modo: string; confirmacaoModoPerigoso: boolean }) =>
      chamar(`${ROTAS.sessoes}/${id}/modo`, {
        method: 'POST',
        corpo: dados,
      }),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: ['sessao', id] }),
    // Pelo código: a mestre do OpenCode recusa com `modo_fixo_da_mestre_opencode`.
    onError: (e) => setErroComando(mensagemDoErro(e, 'Não foi possível trocar o modo.')),
  });

  const trocarModelo = useMutation({
    // `confirmacaoFable` sai do seletor, não de uma constante: com `true` fixo a
    // trava da API (`fable_sem_confirmacao`) nunca recusava nada e a garantia
    // era só da tela. Agora a API recebe o fato — a pessoa apertou Confirmar.
    mutationFn: (dados: { modelo: string; esforco?: string; confirmacaoFable: boolean }) =>
      chamar(`${ROTAS.sessoes}/${id}/modelo`, {
        method: 'POST',
        corpo: dados,
      }),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: ['sessao', id] }),
    // Pelo código: `fable_sem_confirmacao` e as recusas de modelo e esforço
    // já têm frase no catálogo de erros.
    onError: (e) => setErroComando(mensagemDoErro(e, 'Não foi possível trocar o modelo.')),
  });

  /**
   * Acorda uma conversa do painel que dormiu — mesma conversa, mesmo id.
   *
   * A sessão dorme por ociosidade ou quando o daemon reinicia e leva os
   * subprocessos junto. Sem este botão ela virava beco sem saída: não recebia
   * mensagem e não dava para acordar.
   */
  const retomar = useMutation({
    mutationFn: () => chamar(`${ROTAS.sessoes}/${id}/retomar`, { method: 'POST' }),
    onSuccess: () => {
      setErroComando('');
      void cliente.invalidateQueries({ queryKey: ['sessao', id] });
      void cliente.invalidateQueries({ queryKey: ['sessoes'] });
    },
    onError: (e) => setErroComando(mensagemDoErro(e, 'Não foi possível retomar esta conversa.')),
  });

  /**
   * "Continuar aqui": bifurca a conversa do editor numa conversa do painel.
   *
   * A do editor não é tocada — nasce uma nova, com o histórico inteiro e id
   * próprio. Entrar na dela criaria duas linhas disputando a mesma chave e um
   * galho que ninguém mais vê; está medido em
   * `docs/estudos/paridade-com-o-vs-code.md`.
   */
  const continuar = useMutation({
    mutationFn: () =>
      chamar<{ id: string }>(`${ROTAS.sessoes}/${id}/continuar`, { method: 'POST', corpo: {} }),
    onSuccess: (nova) => {
      void cliente.invalidateQueries({ queryKey: ['sessoes'] });
      aoIrParaSessao?.(nova.id);
    },
    onError: (e) => setErroComando(mensagemDoErro(e, 'Não foi possível continuar esta conversa.')),
  });

  /**
   * Trocar de sessão zera o que é DESTA tela, não da conversa.
   *
   * Os dados (eventos, disco, cursor) o gancho já reinicia sozinho quando o
   * `id` muda. O que sobrou aqui é o que pertence ao desenho: a mensagem
   * começada para a outra pessoa, a rolagem e o "visto até aqui".
   */
  useEffect(() => {
    setMensagem(recuperarMensagem(id));
    grudado.current = true;
    setNoFim(true);
    setSeqVisto(0);
    // A fila NÃO se zera aqui: ela mora por conversa no store de envios, e é
    // justamente o que tem de estar esperando quando a pessoa voltar.
  }, [id]);

  /**
   * Os subagentes desta conversa, para a tela deles.
   *
   * Vem do transcript, não da lista viva do stream: a lista viva some quando a
   * sessão sai do ar, e o histórico do que os subagentes fizeram continua
   * valendo depois. As duas se casam pelo `toolUseId` — a viva acrescenta
   * estado e o id que permite parar, e só.
   */
  const subagentes = useMemo(
    () => itens.filter((i) => i.tipo === 'subagente'),
    [itens],
  ) as Extract<ItemDeChat, { tipo: 'subagente' }>[];
  /** A chave do subagente aberto na tela de agentes; vazio quer dizer fechada. */
  const [agenteAberto, setAgenteAberto] = useState('');

  const [exibicao, setExibicao] = useState<Exibicao>(() => lerExibicao());
  // Enquanto esta conversa está na tela, o aparelho não apaga sozinho.
  usarTelaAcesa(exibicao.telaAcesa);
  function mudarExibicao(mudanca: Partial<Exibicao>): void {
    setExibicao((atual) => {
      const novo = { ...atual, ...mudanca };
      gravarExibicao(novo);
      return novo;
    });
  }

  const visiveis = useMemo(() => filtrarPorExibicao(itens, exibicao), [itens, exibicao]);

  /**
   * O que já foi visto, por `seq`.
   *
   * Contar "não vistas" pelo tamanho da lista não serve: carregar mensagens
   * anteriores também aumenta a lista, e anunciar duzentas novidades por causa
   * de histórico antigo seria mentira. O `seq` só cresce para frente.
   */
  const [noFim, setNoFim] = useState(true);
  const [seqVisto, setSeqVisto] = useState(0);
  const naoVistas = eventos.reduce((total, e) => (e.seq > seqVisto ? total + 1 : total), 0);

  /**
   * Sai da fila quando aparece na conversa.
   *
   * Compara pelo começo, não por igualdade: o daemon acrescenta a lista de
   * anexos ao texto antes de entregar, então a mensagem que volta é a sua com
   * um rabo.
   */
  useEffect(() => {
    if (fila.length === 0) return;
    const ditas = itens
      .filter((i) => i.tipo === 'texto' && i.autor === 'voce')
      .map((i) => (i as { texto: string }).texto.trim());
    tirarMensagensJaVistas(id, ditas);
  }, [itens, fila, id]);

  /**
   * "Trabalhando" é inferência, não aviso da máquina.
   *
   * Uma sessão do VS Code não nos conta quando começa e termina um turno; o
   * que dá para ver é o transcript crescendo. Evento recente numa sessão ativa
   * quer dizer que ela está fazendo alguma coisa — e é o suficiente para a
   * pessoa saber que vale esperar.
   */
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    const relogio = setInterval(() => setAgora(Date.now()), 5_000);
    return () => clearInterval(relogio);
  }, []);

  const ultimoEm = eventos.at(-1)?.criadoEm;
  const trabalhando =
    sessao?.estado === 'ATIVA' &&
    Boolean(ultimoEm) &&
    agora - new Date(ultimoEm as string).getTime() < 25_000;

  function irParaOFim(): void {
    grudado.current = true;
    setNoFim(true);
    setSeqVisto(maiorSeq);
    fim.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }

  // Só acompanha o fim se a pessoa já estava no fim: rolar para trás e ser
  // puxado de volta a cada mensagem nova é insuportável.
  useEffect(() => {
    if (!grudado.current) return;
    fim.current?.scrollIntoView({ block: 'end' });
    setSeqVisto(maiorSeq);
  }, [visiveis.length, maiorSeq]);

  /**
   * Devolve a leitura ao ponto exato depois que o histórico entra por cima.
   *
   * Antes do paint, de propósito: com `useEffect` a tela pisca no lugar
   * errado por um quadro antes de saltar de volta.
   */
  useLayoutEffect(() => {
    const area = areaDeRolagem.current;
    const marca = ancora.current;
    if (!area || !marca) return;
    ancora.current = null;
    area.scrollTop = area.scrollHeight - marca.altura + marca.topo;
  }, [visiveis.length]);

  const operador = papel === 'ADMIN' || papel === 'OPERADOR';
  const viva = sessao !== undefined && sessao.estado !== 'ENCERRADA';
  /*
    O motor da sessão: o selo (E25), o catálogo de modelos (E27), a troca e o
    aviso de imagem. `null` enquanto a sessão não chegou — e não "o padrão da
    casa": a conversa do Codex abria dizendo "Claude Code" até a resposta
    chegar (revisão do PR 58, 2.2.15). Sessão que CHEGOU sem o campo é de
    antes do segundo motor, e essa sim é Claude; valor desconhecido também é
    `null`, e nunca mais "o que não é Codex é Claude" (F17).
  */
  const motorDaSessao: Motor | null = sessao ? motorDaLinha(sessao.motor) : null;
  const observada = sessao?.origem === 'OBSERVADA';

  // Comandos e skills para a barra `/`: só sessão do painel os tem — em
  // sessão observada o Claude Code ignora `/` vindo de outro processo, e
  // oferecer o menu seria botão que não faz nada.
  const { capacidades } = usarCapacidades(id, sessao?.origem === 'SDK' && viva);
  // Sessão observada também aceita mensagem: ela vai pelo canal local do
  // Claude Code, o mesmo que a extensão do VS Code usa. Interromper e encerrar
  // continuam de fora — quem manda nessa sessão é quem está no editor.
  const podeComandar = operador && viva;
  const daSdk = sessao?.origem === 'SDK';

  // O botão "continuar aqui" só existe onde a máquina disse que existe: a
  // chave `CONTINUAR_AQUI` do daemon atravessa a sonda e chega aqui como
  // veredito. Sem aprovação o botão NÃO aparece — ausência, não botão cinza.
  const podeContinuar =
    capacidadeDaMaquina(sessao?.maquina?.capacidades, CHAVES_DE_CAPACIDADE.continuarAqui)?.estado === 'aprovada';
  // A volta vale para qualquer conversa do painel numa máquina com editor: o
  // arquivo dela fica na pasta do projeto, e o histórico do Claude no VS Code
  // lista o que está ali — medido, inclusive para conversa que nasceu fora.
  const temEditor = sessao?.maquina?.papel === 'PC';
  // Um só estado: os dois blocos são exclusivos — uma conversa ou é do editor
  // ou é do painel, nunca as duas.
  const [mostrandoDetalhe, setMostrandoVolta] = useState(false);
  // Dormiu: o processo daquela conversa não existe mais na máquina. Dá para
  // acordar, e é a única coisa que faz sentido oferecer aqui.
  const dormindo = daSdk && sessao?.estado === 'DORMINDO';

  /*
    Continuar noutro motor (D19). Com três motores o destino é ESCOLHIDO, num
    seletor dentro do modal (`TrocaDeMotor.tsx`), e cada destino fechado
    aparece lá com o motivo — a mesma régua da tela de criar.

    O botão só EXISTE com algum destino aberto (o padrão do "continuar aqui":
    ausência, nunca botão cinza). A MESTRE fica fora: ela tem identidade por
    motor e gesto próprio — e a API recusa com `mestre_nao_troca_motor` de todo
    jeito.
  */
  const destinosDaTroca = usarDestinosDaTroca(sessao, motorDaSessao, daSdk && operador && viva);
  const podeTrocarMotor =
    sessao?.funcao !== 'MESTRE' && destinosDaTroca.some((d) => d.fechado === null);
  const [trocandoMotor, setTrocandoMotor] = useState(false);
  /*
    O modelo do OpenCode sai do catálogo do provedor ativo (D12), com busca — num
    modal próprio, e não no `Seletor` da barra. O estado mora AQUI, e não no
    botão: no telefone as ações vivem num painel que fecha ao tocar fora, e o
    modal (num portal) iria junto.
  */
  const modeloPeloCatalogoDaFrota =
    motorDaSessao !== null && origemDoCatalogo(motorDaSessao) === 'do-provedor';
  /** O provedor de modelos desta conversa (F2): o selo e o custo do cabeçalho o nomeiam. */
  const provedorDestaConversa = provedorDaConversa(motorDaSessao, sessao?.modelo);
  const modoFixoDaMestre =
    sessao?.funcao === 'MESTRE' && motorDaSessao !== null && modoDaMestreEhFixo(motorDaSessao);
  const [trocandoModelo, setTrocandoModelo] = useState(false);
  /** O detalhe do uso no telefone, onde ele nasce fechado; no desktop está sempre à vista. */
  const [usoAberto, setUsoAberto] = useState(false);

  /**
   * O nome escolhido à mão vence o título que a IA deu, que vence o nome que a
   * máquina se deu. Mesma cadeia da lista e do push, vinda do compartilhado.
   */
  const titulo = sessao ? tituloDaSessao(sessao) : 'Sessão';
  const linkClaude = sessao?.bridgeSessionId
    ? 'https://claude.ai/code/' + sessao.bridgeSessionId
    : null;

  const [confirmar, dialogo] = usarConfirmacao();

  return (
    <div className="flex h-full min-h-0 flex-col">
      {dialogo}
      {trocandoMotor && sessao && motorDaSessao ? (
        <ModalDeTrocaDeMotor
          sessao={sessao}
          origem={motorDaSessao}
          aoFechar={() => setTrocandoMotor(false)}
          aoIrParaSessao={aoIrParaSessao}
        />
      ) : null}
      {trocandoModelo && sessao ? (
        <ModalDeModeloOpencode sessao={sessao} aoFechar={() => setTrocandoModelo(false)} />
      ) : null}
      <header className="shrink-0 border-b border-zinc-800 px-3 py-2">
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="flex min-w-0 items-baseline gap-1.5 text-sm font-medium text-zinc-100">
            <span className="truncate" title={titulo}>
              {titulo}
            </span>
            {/*
              O selo do motor: TEXTO, nunca emoji e nunca só cor — quem não
              distingue as duas cores (ou lê por leitor de tela) ficaria sem a
              informação que decide qual modelo a sessão aceita.

              `shrink-0` com o título `truncate` ao lado: num telefone de 400px
              é o TÍTULO que encolhe, e não o selo que sai da tela levando o
              motor junto.
            */}
            {motorDaSessao && (
              <span
                data-teste="selo-de-motor"
                title={'Esta conversa roda no ' + ROTULO_DO_MOTOR[motorDaSessao]}
                className={
                  'shrink-0 rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide ' +
                  seloDoMotor(motorDaSessao).cor
                }
              >
                {ROTULO_DO_MOTOR[motorDaSessao]}
              </span>
            )}
          </h2>
          <div className="flex shrink-0 items-center gap-2">
            {/*
              O caminho para a tela de agentes a partir do cabeçalho, e não só
              do cartão na conversa: um subagente disparado há mil linhas ficou
              longe demais para ser encontrado rolando, e é justamente o que
              ainda está trabalhando que se quer olhar.
            */}
            {subagentes.length > 0 ? (
              <button
                type="button"
                onClick={() => setAgenteAberto(subagentes[subagentes.length - 1]?.chave ?? '')}
                title="Ver os subagentes desta conversa"
                className="flex items-center gap-1 rounded-md border border-violet-900/70 px-1.5 py-0.5 text-xs text-violet-200 transition hover:bg-violet-950/40"
              >
                <IconeAgente tamanho={13} />
                Agentes ({subagentes.length})
                {subagentes.some((a) => a.aberto) ? (
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-violet-400" />
                ) : null}
              </button>
            ) : null}
            {linkClaude ? (
              <a
                href={linkClaude}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-zinc-500 hover:text-zinc-300 hover:underline"
              >
                claude.ai
              </a>
            ) : null}
            <ResumoDoUso sessaoId={id} aberto={usoAberto} aoAlternar={() => setUsoAberto((v) => !v)} />
            <MenuDeExibicao
              exibicao={exibicao}
              aoMudar={mudarExibicao}
              {...(sessao && sessao.origem === 'SDK' && viva
                ? {
                    daConversa: {
                      compactacaoAutomatica: sessao.compactacaoAutomatica !== false,
                      // O comando é de OPERADOR; a mestre é assunto de ADMIN (a API recusa o resto).
                      podeMudar:
                        podeComandar && (sessao.funcao !== 'MESTRE' || papel === 'ADMIN'),
                      aoMudarCompactacao: (ligada: boolean) => trocarCompactacao.mutate(ligada),
                    },
                  }
                : {})}
            />
          </div>
        </div>
        <p className="truncate font-mono text-xs text-zinc-600" title={sessao?.cwd}>
          {sessao?.cwd}
        </p>
        <p className="mt-0.5 text-xs text-zinc-500">
          {sessao?.maquina.nome} · {sessao?.estado.toLowerCase()} ·{' '}
          {sessao?.origem === 'SDK' ? 'iniciada pelo painel' : (sessao?.entrypoint ?? 'observada')}
        </p>
        {/*
          A exceção da casa à vista (D11): cada turno desta conversa vai ao
          provedor de modelos dela (F2: o do modelo da conversa) e ao provedor do
          modelo. Linha própria, e não ao lado do selo do motor: no telefone os
          dois juntos espremeriam o título a nada.
        */}
        {motorDaSessao && motorSaiDeCasa(motorDaSessao) ? (
          <SeloForaDeCasa className="mt-1" provedor={provedorDestaConversa} />
        ) : null}
        {/*
          A linhagem da troca de motor: esta conversa nasceu de outra, e o
          caminho de volta é um clique — a antiga fica arquivada, não some.
        */}
        {sessao?.continuadaDeId && aoIrParaSessao ? (
          <button
            type="button"
            onClick={() => aoIrParaSessao(sessao.continuadaDeId!)}
            className="mt-0.5 text-xs text-zinc-500 underline hover:text-zinc-300"
          >
            continuada de outra conversa
          </button>
        ) : null}
        {/*
          "Compactar" pede ao daemon o MESMO caminho do vigia (foco da ficha da
          tarefa, sem recado à mestre), e só onde o comando existe: sessão do
          painel que anuncia `/compact`. Em observada o CLI ignora `/` vindo de
          outro processo, e no Codex a compactação é outro gesto — botão ali
          seria botão que não faz nada. Vale com o automático desligado.
        */}
        <DetalheDoUso
          sessaoId={id}
          aberto={usoAberto}
          compactando={comandar.isPending && comandar.variables === 'compactar'}
          provedorOpencode={provedorDestaConversa}
          {...(podeComandar && !dormindo && capacidades?.comandos.some((c) => c.nome === 'compact')
            ? { aoCompactar: () => comandar.mutate('compactar') }
            : {})}
        />
      </header>

      <div className="relative flex min-h-0 flex-1 flex-col">
      <section
        // Endereçável como o `#compositor`, e pelo mesmo motivo: distinguir o
        // que está NA conversa do que está na tira de espera logo acima do
        // campo. Sem isso, um ensaio que procura o texto na tela acha os dois
        // e não sabe dizer qual dos dois faltava.
        id="conversa"
        className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3"
        ref={areaDeRolagem}
        onScroll={(e) => {
          const alvo = e.currentTarget;
          const perto = alvo.scrollHeight - alvo.scrollTop - alvo.clientHeight < 120;
          grudado.current = perto;
          setNoFim(perto);
          // Chegou ao fim: o que estava esperando passou a ser visto.
          if (perto) setSeqVisto(maiorSeq);

          // Rolagem infinita, mas só no que está no banco: ler o disco da
          // máquina é caro e ela pode estar desligada — para essa parte o
          // botão continua explícito, e um erro repetido a cada quadro de
          // rolagem seria pior do que um clique.
          // A guarda de "já estou buscando" mora dentro de
          // `carregarAnteriores`, que sai na primeira linha se houver uma busca
          // em voo. Era a mesma checagem feita duas vezes; ficou uma.
          if (alvo.scrollTop < 300 && temMais) {
            void carregarAnteriores();
          }
        }}
      >
        {temMais || temMaisNoDisco ? (
          <button
            onClick={() => void carregarAnteriores()}
            disabled={carregando}
            className="mx-auto block rounded-md border border-zinc-700 px-3 py-1 text-xs text-zinc-400 transition hover:bg-zinc-800 disabled:opacity-40 pointer-coarse:min-h-11 pointer-coarse:px-4 pointer-coarse:text-sm"
          >
            {carregando
              ? 'carregando…'
              : temMais
                ? 'carregar mensagens anteriores'
                : 'carregar do computador'}
          </button>
        ) : null}

        {erroDisco ? <p className="text-center text-xs text-amber-300">{erroDisco}</p> : null}

        {itens.length === 0 ? (
          sessao && sessao.maquina.estado !== 'ONLINE' ? (
            <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4 text-sm text-zinc-400">
              <p className="text-zinc-200">Sem conteúdo para mostrar.</p>
              <p className="mt-1">
                A conversa desta sessão fica no disco de <strong>{sessao.maquina.nome}</strong>, e
                essa máquina está desligada. Ligue o agente nela e o conteúdo aparece aqui.
              </p>
            </div>
          ) : (
            <p className="text-sm text-zinc-500">
              Nada registrado ainda. Use o botão acima para trazer a conversa do computador.
            </p>
          )
        ) : (
          visiveis.map((item) => (
            <Bolha
              key={item.chave}
              item={item}
              aoAbrirArquivo={aoAbrirArquivo}
              aoIrParaSessao={aoIrParaSessao}
              cwd={sessao?.cwd ?? ''}
              sessaoId={id}
              maquinaId={sessao?.maquina?.id ?? ''}
              diffPreso={exibicao.diffAberto}
              aoPrenderDiff={(preso) => mudarExibicao({ diffAberto: preso })}
              tarefas={tarefas}
              aoAbrirAgente={(chave) => setAgenteAberto(chave)}
              {...(podeComandar ? { aoPararTarefa: (t: string) => pararTarefa.mutate(t) } : {})}
            />
          ))
        )}
        {/*
          A resposta crescendo — no painel também, não só na Prosa.

          O ganho é da casa inteira: quem espera diante do computador espera
          igual. Bolha provisória, e `usarConversa` a descarta assim que a
          versão do transcript chega — nunca as duas ao mesmo tempo.
        */}
        {parcial ? (
          <div
            data-testid="conversa-parcial"
            className="max-w-[85%] self-start rounded-lg bg-zinc-900/60 px-3 py-2 text-sm text-zinc-200"
          >
            {parcial}
            <span className="ml-1 inline-block h-3 w-1.5 animate-pulse bg-zinc-400 align-middle" />
          </div>
        ) : null}
        <div ref={fim} />
      </section>

      {/*
        Quem rolou para trás para ler não pode ser puxado de volta a cada
        mensagem — mas também não pode ficar sem saber que chegou coisa nova.
        O botão só aparece fora do fim, e diz quantas esperam.
      */}
      {!noFim ? (
        <button
          onClick={irParaOFim}
          className="absolute inset-x-0 bottom-3 mx-auto flex w-fit items-center gap-1.5 rounded-full border border-zinc-700 bg-zinc-900/95 px-3 py-1.5 text-xs text-zinc-200 shadow-lg shadow-black/40 backdrop-blur transition hover:bg-zinc-800"
        >
          <IconeDescer tamanho={14} />
          {naoVistas > 0
            ? `${naoVistas} nova${naoVistas > 1 ? 's' : ''}`
            : 'ir para a última mensagem'}
        </button>
      ) : null}

      {agenteAberto ? (
        <PainelDeAgentes
          subagentes={subagentes}
          tarefas={tarefas}
          selecionado={agenteAberto}
          aoSelecionar={setAgenteAberto}
          aoFechar={() => setAgenteAberto('')}
          cwd={sessao?.cwd ?? ''}
          {...(aoAbrirArquivo ? { aoAbrirArquivo } : {})}
          {...(podeComandar ? { aoPararTarefa: (t: string) => pararTarefa.mutate(t) } : {})}
        />
      ) : null}
      </div>

      {/* Fixo acima do compositor: numa conversa longa o cartão sumia na
          rolagem, e um pedido que ninguém vê trava a sessão em silêncio.
          Um por vez, com teto de altura e botão de minimizar — a pilha antiga
          transbordava o painel e levava o botão de agir junto. */}
      <PilhaDePedidos
        pedidos={pedidos}
        ocupado={responder.isPending}
        aoDecidir={(pedidoId, resposta) => responder.mutate({ pedidoId, resposta })}
        {...(aoAbrirArquivo ? { aoAbrirArquivo } : {})}
      />

      {/*
        `data-testid` porque o texto da linha não é dela sozinha.

        Enquanto a mensagem não é registrada pela máquina, o MESMO texto está
        em três lugares ao mesmo tempo: aqui, no `<span>` de realce do
        compositor e dentro do próprio `<textarea>`. Um ensaio que procure esse
        texto na página inteira não tem como dizer qual dos três achou — e o
        que ele queria afirmar é sobre este, o da tira.
      */}
      {trabalhando || fila.length > 0 ? (
        <div
          data-testid="tira-de-espera"
          className="shrink-0 space-y-1.5 border-t border-zinc-800 px-3 py-2 text-xs"
        >
          {trabalhando ? (
            <p className="flex items-center gap-2 text-zinc-500">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
              trabalhando…
            </p>
          ) : null}

          {fila.map((item) => {
            // Falhou: o `POST` recusou (`erro`) ou a máquina disse que não entregou.
            const motivo = motivoDaFalha(item);
            const semConfirmacao = item.desfecho?.tipo === 'sem-confirmacao';
            return (
              <div
                key={item.id}
                className={
                  'flex items-center gap-2 rounded border px-2 py-1 ' +
                  (motivo !== null
                    ? 'border-amber-800 bg-amber-950/30 text-amber-200'
                    : semConfirmacao
                      ? 'border-sky-900 bg-sky-950/30 text-sky-200'
                      : 'border-zinc-800 bg-zinc-900/60 text-zinc-400')
                }
              >
                <span className="shrink-0 text-zinc-600">
                  {motivo !== null || semConfirmacao ? (
                    <IconeAviso tamanho={13} />
                  ) : (
                    <IconeEsperando tamanho={13} />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate" title={item.texto}>
                    {item.acordando && motivo === null ? (
                      <span className="text-zinc-500">acordando a conversa… </span>
                    ) : null}
                    {item.texto}
                  </span>
                  {/*
                    As frases de baixo ficam em linha própria e SEM `truncate`: são
                    o único aviso de que a mensagem já saiu e o que falta é a máquina
                    baixar o anexo (ou de que ela não chegou), e cortadas em 390 px
                    não diriam nada. `role="status"` para o leitor de tela anunciar
                    a mudança sem roubar o foco.
                  */}
                  {item.entrega ? (
                    <span
                      role="status"
                      data-testid="entregando-anexos"
                      className="block break-words text-zinc-500"
                    >
                      A máquina está recebendo os anexos ({tamanhoLegivel(item.entrega.bytes)})…
                    </span>
                  ) : null}
                  {motivo !== null ? (
                    <span role="status" data-testid="falha-do-envio" className="block break-words">
                      {motivo}
                    </span>
                  ) : null}
                  {semConfirmacao ? (
                    <span role="status" data-testid="sem-confirmacao" className="block break-words">
                      {FRASE_SEM_CONFIRMACAO}
                    </span>
                  ) : null}
                </span>
                {motivo !== null ? (
                  <button
                    onClick={() => {
                      reabrirMensagem(item.sessaoId, item.id);
                      enviar.mutate(item);
                    }}
                    className="shrink-0 underline hover:text-amber-100"
                  >
                    tentar de novo
                  </button>
                ) : null}
                {/*
                  Tira da VISTA, não da fila da máquina.

                  O envio sai no mesmo gesto que põe a linha aqui — não existe
                  caminho de volta para uma mensagem já entregue, e a fila que
                  importa é a do Claude Code, do outro lado. Dizer "tirar da
                  fila" prometia um cancelamento que este botão nunca fez: quem
                  clicasse numa mensagem já respondida acreditaria tê-la
                  desfeito. Só some a linha; a mensagem segue valendo.
                */}
                <button
                  onClick={() => tirarMensagem(item.sessaoId, item.id)}
                  title={
                    motivo !== null ? 'Descartar esta mensagem' : 'Tirar da vista (a mensagem já saiu)'
                  }
                  aria-label={
                    motivo !== null ? 'Descartar esta mensagem' : 'Tirar da vista (a mensagem já saiu)'
                  }
                  className="shrink-0 text-zinc-600 hover:text-zinc-200"
                >
                  <IconeFechar tamanho={13} />
                </button>
              </div>
            );
          })}
        </div>
      ) : null}

      {/*
        Dormiu: o painel diz o que houve e os dois jeitos de voltar. Desde
        2026-09-25 mandar mensagem já acorda (a API chama o mesmo Retomar); o
        botão fica para quem quer acordar sem dizer nada.
      */}
      {dormindo && operador ? (
        <div className="shrink-0 space-y-2 border-t border-zinc-800 px-3 py-2">
          <p className="text-xs text-zinc-400">
            Esta conversa dormiu — o processo dela não está mais na máquina. Mande uma mensagem e
            ela acorda sozinha, com o histórico inteiro, na mesma conversa; ou use Retomar sem
            mandar nada.
          </p>
          <BotaoLeve onClick={() => retomar.mutate()} disabled={retomar.isPending}>
            {retomar.isPending ? 'retomando…' : 'Retomar conversa'}
          </BotaoLeve>
        </div>
      ) : null}

      {podeComandar ? (
        <>
          {erroComando ? (
            <p className="shrink-0 border-t border-zinc-800 px-3 py-2 text-sm text-amber-300">
              {erroComando}
            </p>
          ) : null}
          <Compositor
            // Já existe uma linha logo acima (fila, aviso ou erro): uma segunda
            // encostada nela vira um risco duplo com o conteúdo espremido no
            // meio, que foi como ficou na primeira versão.
            semBorda={trabalhando || fila.length > 0 || Boolean(erroComando)}
            sessaoId={id}
            texto={mensagem}
            aoMudarTexto={(texto) => {
              setMensagem(texto);
              // Guarda a cada tecla: trocar de tela no meio de uma frase e
              // voltar para um campo vazio é o tipo de perda que ninguém
              // perdoa duas vezes.
              guardarMensagem(id, texto);
            }}
            enviando={enviar.isPending}
            entregando={entregando}
            aoEnviar={enfileirar}
            // O clipe aparece em todo motor; no OpenCode, com imagem na fila, a
            // pessoa fica sabendo que o turno vai ao modelo de visão (R8).
            avisoDeImagem={motorDaSessao ? avisoDeImagemDoMotor(motorDaSessao) : null}
            comandos={daSdk ? (capacidades?.comandos ?? []) : []}
            motivoSemComandos={
              daSdk
                ? undefined
                : 'o Claude Code ignora "/" vindo de outro processo — digite na janela do VS Code'
            }
            nota={
              daSdk ? null : (
                // Texto visível, não `title`: o atributo não existe no celular,
                // e é justamente lá que a regra da casa manda a conversa morar.
                // A frase antiga dava a impressão de que tudo estava capado.
                <>
                  Sessão observada: dá para mandar recado, anexo, e responder permissão, pergunta e
                  plano. Comando de barra, modelo e modo só em sessão iniciada pelo painel.
                </>
              )
            }
            controles={
              daSdk ? (
                <>
                  <BotaoLeve onClick={() => comandar.mutate('interromper')}>Interromper</BotaoLeve>
                  <BotaoLeve
                    onClick={() =>
                      confirmar({
                        titulo: 'Encerrar sessão',
                        texto:
                          'A sessão ' +
                          (motorDaSessao ? 'do ' + ROTULO_DO_MOTOR[motorDaSessao] + ' ' : '') +
                          'é fechada nesta máquina. A conversa fica guardada, mas não dá para escrever nela de novo.',
                        rotuloConfirmar: 'Encerrar',
                        perigosa: true,
                        aoConfirmar: () => comandar.mutate('fechar'),
                      })
                    }
                  >
                    Encerrar
                  </BotaoLeve>
                  {/*
                    A troca de motor mora junto de Interromper/Encerrar porque
                    é um gesto da MESMA família: age sobre a sessão inteira.
                    O destino se escolhe no modal; o botão só existe com algum
                    destino aberto.
                  */}
                  {podeTrocarMotor ? (
                    <BotaoLeve onClick={() => setTrocandoMotor(true)}>
                      Continuar em outro motor
                    </BotaoLeve>
                  ) : null}
                  {/* O <span> morto virou seletor: o modo troca daqui (D37). */}
                  <Seletor
                    rotulo={MODOS_PERMISSAO.find((m) => m.id === sessao?.modo)?.rotulo ?? 'modo'}
                    titulo="Modo de permissão da sessão"
                    valor={(sessao?.modo ?? null) as IdModoPermissao | null}
                    acima
                    ocupado={trocarModo.isPending}
                    // A mestre do OpenCode nasce livre e FIXA (Interface congelada
                    // item 2): a pílula diz por que não troca, em vez de oferecer
                    // um gesto que a API recusa.
                    bloqueado={
                      modoFixoDaMestre
                        ? FRASES_DOS_ERROS_DO_OPENCODE[CODIGOS_ERRO.modoFixoDaMestreOpencode]
                        : undefined
                    }
                    opcoes={MODOS_PERMISSAO.filter(
                      /*
                        O seletor AO VIVO oferecia os seis, sempre — inclusive
                        `plan` numa conversa do Codex, que a API recusa com
                        codigo proprio. Aqui ele passa a oferecer so o que
                        aquele motor NAQUELA maquina aceita: na VPS o Codex
                        perde `bypassPermissions`, porque la o sandbox e a
                        unica barreira do motor e esse modo e o que o desliga.

                        Sem a sessao carregada nao ha o que filtrar: a lista
                        inteira aparece, e quem recusa continua sendo a API.
                      */
                      (m) =>
                        !motorDaSessao ||
                        (modosDoMotor(motorDaSessao).includes(m.id as IdModoPermissao) &&
                          modoDisponivelNaMaquina(
                            m.id as IdModoPermissao,
                            motorDaSessao,
                            sessao?.maquina.papel === 'VPS' ? 'VPS' : 'PC',
                            sessao?.maquina.bypassTotal === true,
                          )),
                    ).map((m) => ({
                      id: m.id,
                      rotulo: m.rotulo,
                      perigoso: m.perigoso,
                      ...(m.perigoso && papel !== 'ADMIN'
                        ? {
                            desabilitada: true,
                            motivo: 'modos que dispensam aprovação exigem papel ADMIN',
                          }
                        : {}),
                    }))}
                    confirmacao={(opcao) =>
                      opcao.perigoso
                        ? 'Este modo deixa a sessão agir sem pedir aprovação. Confirmar?'
                        : null
                    }
                    aoEscolher={(modo, _esforco, confirmado) =>
                      trocarModo.mutate({ modo, confirmacaoModoPerigoso: confirmado === true })
                    }
                  />
                  {modeloPeloCatalogoDaFrota && sessao ? (
                    /*
                      OpenCode: o catálogo é do provedor ativo, com busca e
                      favoritos (D12), e o esforço é o do MODELO (R7). O
                      gatilho tem o mesmo título do seletor dos outros motores:
                      é o mesmo lugar, para a mesma pergunta.
                    */
                    <BotaoLeve
                      title="Modelo e esforço da sessão"
                      onClick={() => setTrocandoModelo(true)}
                      disabled={trocandoModelo}
                    >
                      <span className="max-w-[22ch] truncate">{rotuloDoModeloOpencode(sessao)}</span>
                    </BotaoLeve>
                  ) : (
                    <Seletor
                      rotulo={
                        (sessao?.modelo ?? 'modelo') +
                        (sessao?.esforco ? ' · ' + sessao.esforco : '')
                      }
                      titulo="Modelo e esforço da sessão"
                      valor={sessao?.modelo ?? null}
                      acima
                      ocupado={trocarModelo.isPending}
                      /*
                        O motor VEM da sessão (E27). Com `'CLAUDE'` fixo, uma
                        sessão do Codex ganhava o catálogo do Claude e trocar o
                        modelo mandaria um nome da Anthropic para o app-server da
                        OpenAI — que o recusaria, depois de a tela já ter dito
                        que a troca foi feita.

                        E é `combinarModelos` quem sabe a diferença: no Codex ele
                        devolve SÓ o que a máquina reportou. Sem capacidades, a
                        lista vem vazia — e lista vazia é a resposta certa: o
                        seletor não oferece nada em vez de oferecer o que não
                        existe.
                      */
                      opcoes={(motorDaSessao ? combinarModelos(capacidades?.modelos ?? [], motorDaSessao) : []).map((m) => ({
                        id: m.id,
                        rotulo: m.rotulo,
                        perigoso: m.exigeConfirmacao,
                        filhos: m.esforcos.map((e) => ({ id: e, rotulo: e })),
                      }))}
                      confirmacao={(opcao) =>
                        opcao.perigoso
                          ? 'Este modelo cobra crédito extra sem perguntar quando a cota acaba. Trocar mesmo assim?'
                          : null
                      }
                      aoEscolher={(modelo, esforco, confirmado) =>
                        trocarModelo.mutate({
                          modelo,
                          ...(esforco ? { esforco } : {}),
                          confirmacaoFable: confirmado === true,
                        })
                      }
                    />
                  )}
                </>
              ) : (
                // Observada: as pílulas ficam, desabilitadas e com o motivo.
                // Sumir era o que fazia a barra parecer vazia sem dizer de quê.
                // Sem valor no banco não há o que mostrar, e aí o slot não vem:
                // pílula sem valor e sem ação seria enfeite.
                <>
                  {sessao?.modo ? (
                    <Seletor
                      rotulo={MODOS_PERMISSAO.find((m) => m.id === sessao.modo)?.rotulo ?? 'modo'}
                      titulo="Modo de permissão da sessão"
                      valor={null}
                      acima
                      bloqueado="trocar o modo só funciona em sessão iniciada pelo painel"
                      opcoes={[]}
                      aoEscolher={() => undefined}
                    />
                  ) : null}
                  {sessao?.modelo ? (
                    <Seletor
                      rotulo={sessao.modelo + (sessao.esforco ? ' · ' + sessao.esforco : '')}
                      titulo="Modelo e esforço da sessão"
                      valor={null}
                      acima
                      bloqueado="trocar modelo e esforço só funciona em sessão iniciada pelo painel"
                      opcoes={[]}
                      aoEscolher={() => undefined}
                    />
                  ) : null}
                </>
              )
            }
          />
        </>
      ) : null}

      {/*
        Quem vê esta conversa (ADR-0020), só ADMIN. A exceção por sessão saiu
        da tela de contas e veio para dentro da sessão: a pergunta "quem vê
        esta conversa?" se faz olhando a conversa, não percorrendo 192
        caixinhas na lista de contas.
      */}
      {papel === 'ADMIN' && sessao ? <QuemVeEstaConversa sessaoId={sessao.id} /> : null}

      {/*
        Continuar aqui: a conversa do editor vira uma conversa do painel, com o
        histórico inteiro. A do VS Code fica intacta — as duas seguem lado a
        lado, e é por isso que a confirmação diz isso com todas as letras.
      */}
      {observada && operador && sessao && podeContinuar ? (
        <div className="shrink-0 space-y-2 border-t border-zinc-800 px-3 py-2">
          <BotaoLeve onClick={() => setMostrandoVolta((v) => !v)} aria-expanded={mostrandoDetalhe}>
            Continuar aqui
          </BotaoLeve>
          {mostrandoDetalhe ? (
            <div className="space-y-2 rounded-md border border-zinc-700 bg-zinc-900 p-3 text-xs text-zinc-300">
              <p>
                Abre uma conversa nova no painel com todo o histórico desta. A conversa do VS Code
                continua lá, intacta — as duas seguem separadas a partir de agora.
              </p>
              <p className="text-zinc-500">
                Vai rodar com <span className="text-zinc-300">{sessao.modelo ?? 'o modelo padrão'}</span>
                {sessao.esforco ? ' · ' + sessao.esforco : ''}.
              </p>
              <div className="flex gap-2">
                <BotaoLeve onClick={() => continuar.mutate()} disabled={continuar.isPending}>
                  {continuar.isPending ? 'continuando…' : 'Continuar aqui'}
                </BotaoLeve>
                <BotaoLeve onClick={() => setMostrandoVolta(false)}>Cancelar</BotaoLeve>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {/*
        Voltar ao VS Code: leva até a porta, e não promete abrir.
        O link `vscode://…/open?session=` foi testado e só abre conversa que o
        próprio editor criou — a que nasce no painel não abre por ele, embora
        apareça no histórico e abra com um clique. Está medido no estudo.
      */}
      {daSdk && operador && sessao && temEditor ? (
        <div className="shrink-0 space-y-2 border-t border-zinc-800 px-3 py-2">
          <BotaoLeve onClick={() => setMostrandoVolta((v) => !v)} aria-expanded={mostrandoDetalhe}>
            Voltar ao VS Code
          </BotaoLeve>
          {mostrandoDetalhe ? (
            <div className="space-y-2 rounded-md border border-zinc-700 bg-zinc-900 p-3 text-xs text-zinc-300">
              <p>
                Esta conversa também aparece no VS Code. Na janela da pasta{' '}
                <span className="font-mono text-zinc-400">{sessao.cwd}</span>: abra o painel do
                Claude, clique no relógio do histórico e escolha{' '}
                {/*
                  Aqui NÃO vale a cadeia do painel: o texto diz o que procurar
                  no histórico do VS Code, e do outro lado o nome que a pessoa
                  fixou aqui não existe. Vale o rótulo da máquina — o `name` do
                  próprio CLI — e, na falta dele, o título da IA.
                */}
                <span className="text-zinc-100">
                  {sessao.nomeMaquina ?? sessao.tituloIa ?? 'esta conversa'}
                </span>
                . Ela abre com tudo que foi feito aqui.
              </p>
              <p className="text-zinc-500">
                O painel não abre a janela por você: o clique acontece onde você já está, na área de
                trabalho daquele projeto.
              </p>
              <div className="flex gap-2">
                <BotaoLeve
                  onClick={() =>
                    confirmar({
                      titulo: 'Encerrar aqui',
                      texto:
                        'A conversa é encerrada no painel. A sessão que está aberta no editor continua como está.',
                      rotuloConfirmar: 'Encerrar',
                      perigosa: true,
                      aoConfirmar: () => comandar.mutate('fechar'),
                    })
                  }
                >
                  Encerrar aqui
                </BotaoLeve>
                <BotaoLeve onClick={() => setMostrandoVolta(false)}>Fechar</BotaoLeve>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {observada && operador && sessao && aoContinuarNaPasta ? (
        <div className="shrink-0 border-t border-zinc-800 px-3 py-2">
          <button
            onClick={() => aoContinuarNaPasta(sessao)}
            className="text-xs text-zinc-500 underline hover:text-zinc-300"
          >
            abrir uma sessão do painel nesta mesma pasta
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * O que a conversa mostra.
 *
 * Comandos e resultados vêm ligados: são o que a sessão está fazendo. O
 * pensamento vem desligado — é longo e quase sempre não é o que se quer ler,
 * mas quem está depurando um comportamento estranho precisa dele à mão.
 */
function MenuDeExibicao({
  exibicao,
  aoMudar,
  daConversa,
}: {
  exibicao: Exibicao;
  aoMudar: (mudanca: Partial<Exibicao>) => void;
  /** O que é DA SESSÃO, e não do aparelho: ausente onde a conversa não tem o botão. */
  daConversa?: {
    compactacaoAutomatica: boolean;
    podeMudar: boolean;
    aoMudarCompactacao: (ligada: boolean) => void;
  };
}): React.JSX.Element {
  const opcoes: { chave: keyof Exibicao; rotulo: string }[] = [
    { chave: 'ferramentas', rotulo: 'comandos e resultados' },
    { chave: 'pensamento', rotulo: 'pensamento da IA' },
    { chave: 'tarefas', rotulo: 'lista de tarefas' },
    { chave: 'telaAcesa', rotulo: 'manter a tela acesa' },
  ];

  return (
    <details className="relative">
      <summary
        title="O que mostrar na conversa"
        className="flex cursor-pointer list-none items-center justify-center rounded px-1 leading-none text-zinc-500 hover:text-zinc-200 pointer-coarse:h-11 pointer-coarse:w-11 [&::-webkit-details-marker]:hidden"
      >
        <IconeMaisOpcoes tamanho={14} />
      </summary>
      <div className="absolute right-0 z-20 mt-1 w-52 rounded-md border border-zinc-700 bg-zinc-900 p-1.5 text-xs shadow-lg shadow-black/40">
        {/* `CaixaDeMarcar`, e não `<input type="checkbox">` cru: o nativo desenha
            com a cor do sistema operacional e aparece azul-Windows no meio de
            um menu escuro. Era o último que restava no painel. */}
        {opcoes.map((opcao) => (
          <CaixaDeMarcar
            key={opcao.chave}
            marcada={exibicao[opcao.chave]}
            aoMarcar={(marcada) => aoMudar({ [opcao.chave]: marcada })}
            className="rounded px-1.5 py-1 text-zinc-300 hover:bg-zinc-800"
          >
            {opcao.rotulo}
          </CaixaDeMarcar>
        ))}
        {/*
          Seção à parte: as opções de cima são do APARELHO (ficam na memória do
          navegador); esta é da CONVERSA e vale para todo mundo que a vê.
          Desligar desliga só a compactação do Remoto: a do próprio Claude, perto
          do limite, segue valendo e protegida pela ficha da tarefa.
        */}
        {daConversa ? (
          <div className="mt-1.5 border-t border-zinc-800 pt-1.5" data-testid="menu-desta-conversa">
            <p className="px-1.5 pb-1 text-[10px] font-medium uppercase tracking-wide text-zinc-500">
              Desta conversa
            </p>
            <CaixaDeMarcar
              marcada={daConversa.compactacaoAutomatica}
              aoMarcar={daConversa.aoMudarCompactacao}
              desabilitada={!daConversa.podeMudar}
              className="rounded px-1.5 py-1 text-zinc-300 hover:bg-zinc-800"
            >
              <span className="block">Compactação automática do Remoto</span>
              <span className="mt-0.5 block text-[11px] leading-snug text-zinc-500">
                O Claude ainda compacta sozinho perto do limite, guardando a tarefa.
              </span>
            </CaixaDeMarcar>
          </div>
        ) : null}
      </div>
    </details>
  );
}
